import React, { useState, useEffect, useMemo } from 'react';
import { X, Activity, MapPin, Calendar, Gauge, Fuel, AlertTriangle, ArrowUp, ArrowDown, ArrowUpDown, Info, FileSpreadsheet, FileText } from 'lucide-react';
import { traceabilityConfigService } from '../../services/traceabilityConfigService';
import { exportTraceabilityExcel, exportTraceabilityPDF } from '../../utils/exporters/traceabilityExporter';

/**
 * Popup component to show the traceability history of a vehicle
 * 
 * @param {Object} props
 * @param {string} props.vin - The VIN of the vehicle to show
 * @param {boolean} props.isOpen - Whether the popup is open
 * @param {Function} props.onClose - Function to close the popup
 */
const CarTraceabilityPopUp = ({ vehicle, isOpen, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc'); // Default to newest first
    const [activeTooltip, setActiveTooltip] = useState(null);
    const [visible, setVisible] = useState(false);

    const vin = vehicle?.VIN;

    const handleTooltipEnter = (e, content, index) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Decide position based on available space or index (fallback)
        // Prefer bottom (tooltip below icon) unless space is tight
        let position = 'top'; // 'top' means tooltip is BELOW the element (top property set)
        let y = rect.bottom + 10; // Default: below the element

        // If close to bottom of screen (less than 100px space) OR it's one of the last rows
        // Note: checking index is a good heuristic if window size is standard, but rect check is better
        if (spaceBelow < 120) {
            position = 'bottom'; // Tooltip ABOVE the element
            y = rect.top - 10;
        }

        setActiveTooltip({
            x: rect.left + rect.width / 2,
            y: y,
            content: content,
            position: position
        });
    };

    const handleTooltipLeave = () => {
        setActiveTooltip(null);
    };

    useEffect(() => {
        if (isOpen && vin) {
            setLoading(true);
            setError(null);
            traceabilityConfigService.getVehicleTraceability(vin)
                .then(response => {
                    setData(response);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Failed to load traceability data", err);
                    setError("No se pudo cargar la información de trazabilidad.");
                    setLoading(false);
                });
        }
    }, [isOpen, vin]);

    // Lock body scroll when popup is open + trigger entrance animation
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            requestAnimationFrame(() => setVisible(true));
        } else {
            setVisible(false);
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const sortedSegments = useMemo(() => {
        if (!data?.segmentos) return [];
        return [...data.segmentos].sort((a, b) => {
            if (sortOrder === 'asc') {
                return a.segmento - b.segmento;
            } else {
                return b.segmento - a.segmento;
            }
        });
    }, [data, sortOrder]);

    const toggleSort = () => {
        setSortOrder(current => current === 'asc' ? 'desc' : 'asc');
    };

    const handleClose = () => {
        setVisible(false);
        setTimeout(onClose, 300);
    };

    if (!isOpen) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('es-MX', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return dateString;
        }
    };

    const calculateDuration = (start, end) => {
        if (!start || !end) return '-';
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diff = endDate - startDate;

        if (diff < 0) return '0 min';

        const totalMinutes = Math.floor(diff / (1000 * 60));
        const minutes = totalMinutes % 60;
        const totalHours = Math.floor(totalMinutes / 60);
        const hours = totalHours % 24;
        const days = Math.floor(totalHours / 24);

        if (days > 0) {
            return hours > 0
                ? `${days}d ${hours}h ${minutes}m`
                : `${days}d ${minutes}m`;
        }
        if (totalHours === 0) {
            return `${minutes} min`;
        }
        return `${totalHours}h ${minutes}m`;
    };

    // --- Export Logic ---
    const getExportFilters = () => {
        if (!data?.segmentos || data.segmentos.length === 0) {
            return {
                startMonth: '-', startYear: '-',
                endMonth: '-', endYear: '-'
            };
        }

        // Encontrar fechas min y max de los segmentos
        const dates = data.segmentos.flatMap(s => [
            s.origen?.fecha ? new Date(s.origen.fecha) : null,
            s.destino?.fecha ? new Date(s.destino.fecha) : null
        ]).filter(Boolean);

        if (dates.length === 0) {
            return {
                startMonth: 'Unknown', startYear: 'Unknown',
                endMonth: 'Unknown', endYear: 'Unknown'
            };
        }

        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));

        const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

        return {
            startMonth: monthNames[minDate.getMonth()],
            startYear: minDate.getFullYear().toString(),
            endMonth: monthNames[maxDate.getMonth()],
            endYear: maxDate.getFullYear().toString()
        };
    };

    const handleExportExcel = () => {
        if (!sortedSegments.length) return;
        const filters = getExportFilters();

        // Enrich segments with vehicle data for the exporter
        const segmentsToExport = sortedSegments.map(seg => ({
            ...seg,
            vehiculo: {
                vin: vehicle.VIN,
                marca: vehicle.Marca,
                familia: vehicle.Familia || vehicle.Modelo,
                catalogo: vehicle.Catalogo || vehicle.Version
            }
        }));

        exportTraceabilityExcel(segmentsToExport, filters);
    };

    const handleExportPdf = () => {
        if (!sortedSegments.length) return;
        const filters = getExportFilters();

        // Enrich segments with vehicle data for the exporter
        const segmentsToExport = sortedSegments.map(seg => ({
            ...seg,
            vehiculo: {
                vin: vehicle.VIN,
                marca: vehicle.Marca,
                familia: vehicle.Familia || vehicle.Modelo,
                catalogo: vehicle.Catalogo || vehicle.Version
            }
        }));

        exportTraceabilityPDF(segmentsToExport, filters);
    };

    return (
        <div
            className={`fixed inset-0 z-50 transition-colors duration-300 backdrop-blur-sm ${visible ? 'bg-black/50' : 'bg-black/0'}`}
            onClick={handleClose}
        >
            {/* Panel — bottom-sheet on mobile, centered modal on desktop */}
            <div
                className={`fixed z-[10000] flex flex-col bg-white overflow-hidden
                    bottom-0 left-0 right-0 rounded-t-2xl max-h-[92vh]
                    sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:right-auto
                    sm:-translate-x-1/2 sm:-translate-y-1/2
                    sm:w-full sm:max-w-4xl sm:rounded-xl sm:shadow-2xl sm:max-h-[90vh]
                    transition-all duration-300 ease-out
                    ${visible ? 'translate-y-0 opacity-100' : 'translate-y-full sm:translate-y-[-48%] opacity-0 pointer-events-none'}`}
                onClick={e => e.stopPropagation()}
            >

                {/* Header */}
                <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 bg-blue-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 sm:gap-3 min-w-0">
                            <div className="bg-blue-100 p-1.5 sm:p-2 rounded-lg shrink-0 mt-0.5">
                                <Activity className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-base sm:text-xl font-bold text-blue-900 leading-tight">Trazabilidad del Vehículo</h2>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-blue-700 font-medium mt-0.5">
                                    <span className="font-mono text-[11px] sm:text-sm">VIN: {vin}</span>
                                    <span className="text-blue-300 hidden sm:inline">|</span>
                                    <span className="uppercase truncate max-w-[180px] sm:max-w-none">
                                        {vehicle.Marca} {vehicle.Familia || vehicle.Modelo} {vehicle.Año || vehicle.AnoModelo} {vehicle.Catalogo || vehicle.Version}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        {data?.consumo_km_l && (
                                            <span className="bg-white/50 text-blue-800 text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded border border-blue-200 shadow-sm">
                                                {data.consumo_km_l} Km/L
                                            </span>
                                        )}
                                        {data?.advertencias && data.advertencias.length > 0 && (
                                            <div className="relative group flex items-center">
                                                <AlertTriangle className="w-4 h-4 text-orange-500 cursor-help" />
                                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block w-64 bg-gray-800 text-white text-xs rounded-lg p-3 shadow-lg z-50">
                                                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                                                    <div className="relative font-medium mb-1 text-orange-300 flex items-center gap-1">
                                                        <AlertTriangle className="w-3 h-3" />
                                                        Advertencia
                                                    </div>
                                                    <ul className="list-none space-y-1 relative opacity-90">
                                                        {data.advertencias.map((adv, idx) => (
                                                            <li key={idx}>{adv}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                            {/* Export Buttons */}
                            <button
                                onClick={handleExportExcel}
                                disabled={!data?.segmentos?.length}
                                className="p-1.5 sm:p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Exportar a Excel"
                            >
                                <FileSpreadsheet className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                            <button
                                onClick={handleExportPdf}
                                disabled={!data?.segmentos?.length}
                                className="p-1.5 sm:p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Exportar a PDF"
                            >
                                <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>

                            <div className="w-px h-5 sm:h-6 bg-blue-200 mx-0.5 sm:mx-1"></div>

                            <button
                                onClick={handleClose}
                                className="p-1.5 sm:p-2 hover:bg-blue-100 rounded-lg transition-colors text-blue-600"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center items-center h-full py-16">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center h-full text-center p-8">
                            <div className="mb-4">
                                <Activity className="w-16 h-16 text-gray-300" strokeWidth={1} />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-1">
                                No se pudo cargar la información
                            </h3>
                            <p className="text-gray-500">
                                {error || "No se ha encontrado información de trazabilidad para este vehículo."}
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col flex-1 min-h-0 p-3 sm:p-4 md:p-6 gap-3 sm:gap-6">
                            {/* General Consumption Stats - Compact Version */}
                            <div className="flex flex-row bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm items-center gap-3 sm:gap-8 flex-shrink-0">
                                <div className="flex items-center gap-2 shrink-0">
                                    <div className="p-1.5 bg-blue-100 rounded-full">
                                        <Activity className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <span className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide">Consumo General</span>
                                </div>

                                <div className="flex-1 flex items-center justify-around sm:justify-evenly border-l border-gray-200 pl-3 sm:pl-8">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="p-1.5 bg-green-100 rounded-full">
                                            <Gauge className="w-4 h-4 text-green-600" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] text-gray-500 uppercase font-medium">Distancia Total</span>
                                            <span className="text-sm font-bold text-gray-900">
                                                {data?.resumen?.distancia_total_km?.toLocaleString('es-MX', { maximumFractionDigits: 1 }) || '0'} km
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="p-1.5 bg-orange-100 rounded-full">
                                            <Fuel className="w-4 h-4 text-orange-600" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] text-gray-500 uppercase font-medium">Consumo Total</span>
                                            <span className="text-sm font-bold text-gray-900">
                                                {data?.resumen?.consumo_total_litros?.toLocaleString('es-MX', { maximumFractionDigits: 2 }) || '0'} L
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Segments Table */}
                            <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex-1 overflow-hidden flex flex-col min-h-0">
                                <div className="overflow-auto flex-1 h-full">
                                    <table className="min-w-full divide-y divide-gray-200 relative">
                                        <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                            <tr>
                                                <th
                                                    className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors select-none group"
                                                    onClick={toggleSort}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        #
                                                        {sortOrder === 'asc' ?
                                                            <ArrowUp className="w-3 h-3 text-blue-600" /> :
                                                            <ArrowDown className="w-3 h-3 text-blue-600" />
                                                        }
                                                    </div>
                                                </th>
                                                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                                    Origen
                                                </th>
                                                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                                    Destino
                                                </th>
                                                <th className="px-3 sm:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                                    Distancia
                                                </th>
                                                <th className="px-3 sm:px-6 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                                    Consumo
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {sortedSegments.map((segment, index) => (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {segment.segmento}
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center flex-wrap gap-1">
                                                                <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                                                                <span className="text-xs sm:text-sm font-medium text-gray-900">{segment.origen?.patio_nombre || '-'}</span>
                                                                <span className={`inline-flex text-[10px] leading-4 font-semibold rounded-full px-2 w-fit ${segment.origen?.tipo_lectura === 'antena' ? 'bg-purple-50 text-purple-700 border border-purple-100' : 'bg-blue-50 text-blue-700 border border-blue-100'
                                                                    }`}>
                                                                    {segment.origen?.tipo_lectura}
                                                                </span>
                                                            </div>
                                                            <div className="text-[10px] sm:text-xs text-gray-500 mt-1 flex items-center">
                                                                <Calendar className="w-3 h-3 mr-1 shrink-0" />
                                                                <span className="whitespace-nowrap">{formatDate(segment.origen?.fecha)}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center flex-wrap gap-1">
                                                                <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                                                                <span className="text-xs sm:text-sm font-medium text-gray-900">{segment.destino?.patio_nombre || '-'}</span>
                                                                <span className={`inline-flex text-[10px] leading-4 font-semibold rounded-full px-2 w-fit ${segment.destino?.tipo_lectura === 'antena' ? 'bg-purple-50 text-purple-700 border border-purple-100' : 'bg-blue-50 text-blue-700 border border-blue-100'
                                                                    }`}>
                                                                    {segment.destino?.tipo_lectura}
                                                                </span>
                                                            </div>
                                                            <div className="text-[10px] sm:text-xs text-gray-500 mt-1 flex items-center">
                                                                <Calendar className="w-3 h-3 mr-1 shrink-0" />
                                                                <span className="whitespace-nowrap">{formatDate(segment.destino?.fecha)}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {(!segment.distancia_km || segment.distancia_km === 0) && segment.origen?.patio_nombre !== segment.destino?.patio_nombre ? (
                                                        <td colSpan="2" className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                                                            <div
                                                                className="relative group flex justify-center"
                                                                onMouseEnter={(e) => handleTooltipEnter(e, "No se ha configurado la distancia entre estos sitios", index)}
                                                                onMouseLeave={handleTooltipLeave}
                                                            >
                                                                <div className="bg-orange-50 p-1.5 rounded-full cursor-help hover:bg-orange-100 transition-colors">
                                                                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                                                                </div>
                                                            </div>
                                                        </td>
                                                    ) : segment.origen?.patio_nombre === segment.destino?.patio_nombre ? (
                                                        <td colSpan="2" className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                                                            <div className="flex justify-center">
                                                                <div className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[10px] sm:text-xs font-medium">
                                                                    <Info className="w-3 h-3 mr-1" />
                                                                    Mismo sitio · {calculateDuration(segment.origen?.fecha, segment.destino?.fecha)}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    ) : (
                                                        <>
                                                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">
                                                                {segment.distancia_km > 0 ? `${segment.distancia_km.toFixed(1)} km` : '-'}
                                                            </td>
                                                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 text-center">
                                                                {segment.litros_consumidos > 0 ? `${segment.litros_consumidos.toFixed(2)} L` : '-'}
                                                            </td>
                                                        </>
                                                    )}
                                                </tr>
                                            ))}
                                            {(!data?.segmentos || data.segmentos.length === 0) && (
                                                <tr>
                                                    <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                                                        No hay registros de trazabilidad disponibles para este vehículo.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Fixed Tooltip Portal */}
                {activeTooltip && (
                    <div
                        className="fixed z-[100] w-56 bg-gray-800 text-white text-xs rounded-lg p-2 shadow-lg text-center whitespace-normal pointer-events-none transition-opacity duration-150"
                        style={{
                            left: activeTooltip.x,
                            top: activeTooltip.position === 'top' ? activeTooltip.y : undefined,
                            bottom: activeTooltip.position === 'bottom' ? window.innerHeight - activeTooltip.y : undefined,
                            transform: 'translateX(-50%)'
                        }}
                    >
                        <div
                            className={`absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45 ${activeTooltip.position === 'top' ? '-top-1' : '-bottom-1'
                                }`}
                        ></div>
                        {activeTooltip.content}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CarTraceabilityPopUp;